import { useQuery } from '@tanstack/react-query';
import { create, keyResolver, windowedFiniteBatchScheduler } from '@yornaath/batshit';
import { fetchAIVATRegistrationProposals, fetchVATRegistrationProposals } from '~/api/bookkeeping/vat-entries';
import { getEInvoice } from '~/api/invoicing';
import { queryClient } from '~/client/queryClient';
import { Document } from '~/types/document';
import { assertIsDefined } from '~/types/typeGuards';
import { BookkeepingInvoicesQueryKeys, DocumentWithVATRegistrations } from '~/types/vat-bookkeeping';

type DocumentWithEInvoice<T extends Document> = T & Pick<DocumentWithVATRegistrations, 'eInvoice'>;

export const decorateWithEInvoice = async <T extends Document>(document: T): Promise<DocumentWithEInvoice<T>> => {
  try {
    // fetch and cache eInvoice
    const eInvoice = await queryClient.fetchQuery({
      queryKey: BookkeepingInvoicesQueryKeys.eInvoice(document.id),
      // mark request as never stale to avoid refetching
      staleTime: Infinity,
      queryFn: () => getEInvoice(document.id),
    });

    return {
      ...document,
      eInvoice,
    };
  } catch {
    return document;
  }
};

export const decorateDocumentsWithEInvoices = (
  documents: DocumentWithVATRegistrations[],
): Promise<DocumentWithVATRegistrations[]> => {
  return Promise.all(documents.map((document) => decorateWithEInvoice(document)));
};

const vatRegistrationProposal = create({
  fetcher: fetchVATRegistrationProposals,
  resolver: keyResolver('documentId'),
  scheduler: windowedFiniteBatchScheduler({
    windowMs: 50,
    maxBatchSize: 50,
  }),
});

const decorateWithVATRegistrationProposal = async (
  document: DocumentWithVATRegistrations,
): Promise<DocumentWithVATRegistrations> => {
  try {
    const { vatEntries, vatBooks, vatRegistrationDate, source } = await queryClient.fetchQuery({
      queryKey: BookkeepingInvoicesQueryKeys.vatRegistrationProposal(document.id),
      queryFn: () => vatRegistrationProposal.fetch(document.id),
    });

    return {
      ...document,
      vatRegistrationProposal:
        vatEntries.length === 0
          ? undefined
          : {
              vatEntries,
              vatBooks,
              vatRegistrationDate,
              source,
            },
    };
  } catch {
    return document;
  }
};

export const decorateDocumentsWithVATRegistrationProposal = (
  documents: DocumentWithVATRegistrations[],
): Promise<DocumentWithVATRegistrations[]> => {
  return Promise.all(documents.map((document) => decorateWithVATRegistrationProposal(document)));
};

const aiVatRegistrationProposal = create({
  fetcher: fetchAIVATRegistrationProposals,
  resolver: keyResolver('documentId'),
  scheduler: windowedFiniteBatchScheduler({
    windowMs: 50,
    maxBatchSize: 50,
  }),
});

const decorateWithAIVATRegistrationProposal = async (
  document: DocumentWithVATRegistrations,
): Promise<DocumentWithVATRegistrations> => {
  try {
    const { vatEntries, vatBooks, vatRegistrationDate, source } = await queryClient.fetchQuery({
      queryKey: BookkeepingInvoicesQueryKeys.aiVATRegistrationProposal(document.id),
      queryFn: () => aiVatRegistrationProposal.fetch(document.id),
    });

    return {
      ...document,
      vatRegistrationProposal:
        vatEntries.length === 0
          ? undefined
          : {
              vatEntries,
              vatBooks,
              vatRegistrationDate,
              source,
            },
    };
  } catch {
    return document;
  }
};

export const decorateDocumentsWithAIVATRegistrationProposal = (
  documents: DocumentWithVATRegistrations[],
): Promise<DocumentWithVATRegistrations[]> => {
  return Promise.all(documents.map((document) => decorateWithAIVATRegistrationProposal(document)));
};

export const useBookkeepingEInvoice = (
  documentId: Document['id'] | undefined,
  options: {
    enabled: boolean;
  } = { enabled: true },
) =>
  useQuery({
    enabled: options.enabled && Boolean(documentId),
    queryFn: async () => {
      assertIsDefined(documentId, 'documentid must be defined');
      return await getEInvoice(documentId);
    },
    queryKey: BookkeepingInvoicesQueryKeys.eInvoice(documentId ?? ''),
    // avoid refetching at all since it will be called once per document
    staleTime: Infinity,
    refetchInterval: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
